<script setup lang="ts">
import Regex from '@static/regex';
import { TaxiReserveDocument } from '@schema/directus';

const emit = defineEmits<{
    close: [successful?: boolean];
}>();
const { mutate } = useMutation(TaxiReserveDocument);
const route = ref('');
const name = ref('');
const mobileNumber = ref('');
const description = ref('');
const departureDate = ref<Date>();
const departureTime = ref<number>();
const loading = ref(false);
const form = new Form({
    route: new Field(route, {
        required: true,
    }),
    name: new Field(name, {
        required: true,
    }),
    mobileNumber: new Field(mobileNumber, {
        required: true,
        validator: value => Regex.mobile.full.test(value),
        partialValidator: value => Regex.mobile.partial.test(value),
    }),
    description: new Field(description),
    departureDate: new Field(departureDate, {
        required: true,
        reviver: value => (value ? new Date(value) : value),
    }),
    departureTime: new Field(departureTime, {
        required: true,
    }),
});
const departureHours = [...new Array(24)].map((_, i) => ({
    label: i.toString(),
    id: i,
}));
const submit = async () => {
    if (form.validateAll()) {
        const date = dayConfig
            .js(departureDate.value)
            .set('hour', departureTime.value ?? 0)
            .toISOString();
        try {
            loading.value = true;
            await mutate({
                description: description.value,
                name: name.value,
                mobileNumber: mobileNumber.value,
                route: route.value,
                departureTime: date,
            });
            emit('close', true);
        } catch (e) {
            handleException(e);
            emit('close');
        } finally {
            loading.value = false;
        }
    } else {
        nextTick(() => {
            navToSection();
        });
    }
};
</script>
<template>
    <mr-modal
        title="درخواست رزرو تاکسی"
        width="500px"
        mobile-page
        @close="emit('close')"
    >
        <div class="form-container">
            <mr-input
                v-bind="form.fields.name.attrs"
                label="نام و نام خانوادگی"
            />
            <mr-input
                v-bind="form.fields.mobileNumber.attrs"
                label="شماره تماس"
            />
            <mr-input
                v-bind="form.fields.route.attrs"
                label="مسیر مورد درخواست"
            />
            <div class="date-row">
                <mr-datepicker
                    v-bind="form.fields.departureDate.attrs"
                    :min-date="new Date()"
                    start-day-title="تاریخ حرکت"
                    include-year
                />
                <mr-select
                    v-bind="form.fields.departureTime.attrs"
                    :options="departureHours"
                    label="ساعت حرکت"
                    full-width
                />
            </div>
            <mr-input
                v-bind="form.fields.description.attrs"
                label="توضیحات"
                text-area
                full-width
            />
        </div>
        <template #footer>
            <mr-button :loading full-width @click="submit"> تایید </mr-button>
        </template>
    </mr-modal>
</template>
<style scoped lang="scss">
.form-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: 5px;
    .date-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        width: 100%;
        > * {
            flex-basis: calc(50% - 4px);
            flex-grow: 1;
            @include mobile-only(500px) {
                flex-basis: 100%;
            }
        }
        :deep(.datepicker-modal-container) {
            @include desktop-only {
                left: 0;
            }
        }
    }
}
</style>
